import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Mail } from 'lucide-react';

const NAV_ITEMS = [
  { slug: 'inicio',     key: 'nav_home',    href: '/' },
  { slug: 'planes',     key: 'nav_plans',   href: '/#planes' },
  { slug: 'contacto',   key: 'nav_contact', href: '/contacto' },
  { slug: 'privacidad', key: 'nav_privacy', href: '/privacidad' },
  { slug: 'terminos',   key: 'nav_terms',   href: '/terminos' },
  { slug: 'reembolsos', key: 'nav_refunds', href: '/cancelaciones' },
];

// Tiny inline language switcher for the public site (no auth required).
const LangSwitcher = () => {
  const { lang, setLang, languages } = useLanguage();
  const { theme } = useTheme();
  return (
    <div className="flex items-center gap-1" data-testid="public-lang-switcher">
      {languages.map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)}
          data-testid={`public-lang-${l.code}`}
          className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors"
          style={{
            color: lang === l.code ? theme.accent : theme.textMuted,
            background: lang === l.code ? `${theme.accent}15` : 'transparent',
            fontWeight: lang === l.code ? 600 : 400,
          }}>
          {l.code}
        </button>
      ))}
    </div>
  );
};

export const PublicHeader = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md"
      style={{ background: `${theme.bg}D0`, borderBottom: `1px solid ${theme.border}` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/teko-logo.png" alt="Teko" className="w-8 h-8 object-contain" />
          <span className="text-xl font-light tracking-tight" style={{ color: theme.accent }}>Teko</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {NAV_ITEMS.map((n) => {
            const isActive = location.pathname === n.href;
            return (
              <Link
                key={n.href}
                to={n.href}
                className="text-sm transition-colors hover:opacity-80"
                style={{ color: isActive ? theme.accent : theme.textSecondary }}
                data-testid={`public-nav-${n.slug}`}
              >
                {t(n.key)}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LangSwitcher />
          {user ? (
            <Link to="/dashboard" data-testid="header-app-link"
              className="text-sm px-4 py-2 rounded-md font-medium"
              style={{ background: theme.accent, color: theme.bg }}>
              {t('open_teko')}
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link"
                className="text-sm" style={{ color: theme.textSecondary }}>
                {t('footer_login')}
              </Link>
              <Link to="/login?mode=register" data-testid="header-signup-link"
                className="text-sm px-4 py-2 rounded-md font-medium"
                style={{ background: theme.accent, color: theme.bg }}>
                {t('start_free')}
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button onClick={() => setOpen(!open)} className="md:hidden p-2" style={{ color: theme.textPrimary }}
          aria-label={t('menu')} data-testid="public-mobile-menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="md:hidden border-t" style={{ borderColor: theme.border, background: theme.bg }}>
          <div className="px-4 py-3 space-y-1">
            {NAV_ITEMS.map((n) => (
              <Link key={n.href} to={n.href} onClick={() => setOpen(false)}
                data-testid={`public-mobile-nav-${n.slug}`}
                className="block px-3 py-2.5 text-sm rounded-md"
                style={{ color: theme.textSecondary }}>
                {t(n.key)}
              </Link>
            ))}
            <div className="pt-3 mt-2 border-t flex flex-col gap-2" style={{ borderColor: theme.border }}>
              <div className="flex justify-center pb-1"><LangSwitcher /></div>
              {user ? (
                <Link to="/dashboard" onClick={() => setOpen(false)}
                  className="block px-3 py-2.5 text-sm rounded-md text-center font-medium"
                  style={{ background: theme.accent, color: theme.bg }}>
                  {t('open_teko')}
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)}
                    className="block px-3 py-2.5 text-sm rounded-md text-center"
                    style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}>
                    {t('footer_login')}
                  </Link>
                  <Link to="/login?mode=register" onClick={() => setOpen(false)}
                    className="block px-3 py-2.5 text-sm rounded-md text-center font-medium"
                    style={{ background: theme.accent, color: theme.bg }}>
                    {t('start_free')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export const PublicFooter = () => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  return (
    <footer className="mt-24" style={{ background: theme.card, borderTop: `1px solid ${theme.border}` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-3">
              <img src="/teko-logo.png" alt="Teko" className="w-8 h-8 object-contain" />
              <span className="text-xl font-light" style={{ color: theme.accent }}>Teko</span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: theme.textMuted }}>
              {t('footer_tagline')}
            </p>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: theme.textMuted }}>{t('footer_product')}</p>
            <ul className="space-y-2">
              <li><Link to="/#funciones" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_features')}</Link></li>
              <li><Link to="/#planes" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_plans')}</Link></li>
              <li><Link to="/login" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_login')}</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: theme.textMuted }}>{t('footer_legal')}</p>
            <ul className="space-y-2">
              <li><Link to="/privacidad" data-testid="footer-privacy-link" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_privacy_policy')}</Link></li>
              <li><Link to="/terminos" data-testid="footer-terms-link" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_terms_conditions')}</Link></li>
              <li><Link to="/cancelaciones" data-testid="footer-refunds-link" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_refund_policy')}</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: theme.textMuted }}>{t('footer_contact')}</p>
            <ul className="space-y-2">
              <li><Link to="/contacto" data-testid="footer-contact-link" className="text-sm" style={{ color: theme.textSecondary }}>{t('footer_contact_form')}</Link></li>
              <li>
                <a href="mailto:asistencia.teko@gmail.com" data-testid="footer-mailto"
                  className="text-sm flex items-center gap-1.5"
                  style={{ color: theme.accent }}>
                  <Mail size={12} /> asistencia.teko@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: `1px solid ${theme.border}` }}>
          <p className="text-[11px]" style={{ color: theme.textMuted }}>
            © {new Date().getFullYear()} Teko. {t('footer_rights')}
          </p>
          <p className="text-[11px]" style={{ color: theme.textMuted }}>
            {t('footer_payments_secure')}
          </p>
        </div>
      </div>
    </footer>
  );
};

const PublicLayout = ({ children }) => {
  const { theme } = useTheme();
  return (
    <div className="min-h-screen flex flex-col relative" style={{ background: theme.bg, color: theme.textPrimary }}>
      {/* Ambient gradients */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: `
          radial-gradient(circle at 15% 0%, ${theme.primary}22, transparent 45%),
          radial-gradient(circle at 85% 8%, ${theme.accent}15, transparent 40%),
          radial-gradient(circle at 50% 100%, ${theme.primary}15, transparent 50%)
        `,
      }} />
      <div className="relative z-10 flex flex-col flex-1">
        <PublicHeader />
        <main className="flex-1">{children}</main>
        <PublicFooter />
      </div>
    </div>
  );
};

export default PublicLayout;
